import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  Drawer,
  Card,
  Grid,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Button,
  NativeSelectRoot,
  NativeSelectField,
  Alert,
} from '@chakra-ui/react'
import {
  ChevronUp,
  ChevronDown,
  X,
  Trash2,
  User,
  Mail,
  Phone,
  Clock,
  CalendarClock,
  MoreVertical,
  ArrowRightLeft,
  Users,
  UserCheck,
  AlertTriangle,
  Info,
  Plus,
} from 'lucide-react'
import { useUnit, useDeleteUnit, useUpdateUnit, useUnitOwners, useUpdateUnitCommonRep } from '../hooks/useUnits'
import { useExternalContacts, ExternalContactModal } from '@/features/contacts'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EditableField } from '@/shared/components/EditableField'
import { EditableSelectField } from '@/shared/components/EditableSelectField'
import { UnitMutationsManagementModal } from './UnitMutationsManagementModal'

interface UnitDetailDrawerProps {
  unitId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function UnitDetailDrawer({
  unitId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
}: UnitDetailDrawerProps) {
  const { t } = useTranslation()
  const { data: unit, isLoading, error } = useUnit(unitId ?? undefined)
  const { data: ownerships } = useUnitOwners(unitId ?? undefined)
  const { data: externalContacts } = useExternalContacts(unit?.residence_id ?? undefined)
  const { data: auth } = useAuth()
  const deleteUnit = useDeleteUnit()
  const updateUnit = useUpdateUnit()
  const updateCommonRep = useUpdateUnitCommonRep()
  const [mutationsManagementOpen, setMutationsManagementOpen] = useState(false)
  const [selectedRepType, setSelectedRepType] = useState<'none' | 'owner' | 'external'>('none')
  const [selectedRepId, setSelectedRepId] = useState<string | null>(null)
  const [contactModalOpen, setContactModalOpen] = useState(false)

  // Déterminer si le lot est en indivision (plus d'un propriétaire actif)
  const isIndivision = ownerships && ownerships.length > 1

  // Déterminer le mandataire actuel
  const hasCommonRep = unit?.common_rep_owner_id || unit?.common_rep_contact_id
  const commonRepOwner = unit?.common_rep_owner as { id: string; first_name: string; last_name: string; email?: string } | null
  const commonRepContact = unit?.common_rep_contact as { id: string; first_name: string; last_name: string; email?: string } | null

  // Check if user can delete this unit
  // Only ADMIN and MANAGER can delete units
  const canDelete = () => {
    if (!unit || !auth) return false

    // ADMIN can delete all units
    if (auth.isAdmin) return true

    // MANAGER can delete units in their managed residences
    return auth.isManager(unit.residence_id)
  }

  // Options pour le type de lot
  const unitTypeOptions = [
    { value: 'RESIDENTIAL', label: t('units.unit_types.RESIDENTIAL') },
    { value: 'PARKING', label: t('units.unit_types.PARKING') },
    { value: 'CELLAR', label: t('units.unit_types.CELLAR') },
    { value: 'COMMERCIAL', label: t('units.unit_types.COMMERCIAL') },
  ]

  // Labels pour le type de propriété
  const ownershipTypeConfig: Record<string, { label: string; color: string }> = {
    full: { label: 'Pleine propriété', color: 'green' },
    usufruct: { label: 'Usufruit', color: 'orange' },
    bare: { label: 'Nue-propriété', color: 'purple' },
  }

  const handleFieldUpdate = async (fieldName: string, value: string | number) => {
    if (!unitId) return

    try {
      await updateUnit.mutateAsync({
        id: unitId,
        [fieldName]: value || null,
      })
    } catch (error) {
      console.error('Error updating unit:', error)
      alert(t('common.error'))
    }
  }

  const handleDelete = async () => {
    if (!unitId) return
    if (!window.confirm(t('units.delete_confirm'))) return

    try {
      await deleteUnit.mutateAsync(unitId)
      onClose()
    } catch (error) {
      console.error('Error deleting unit:', error)
      alert(t('common.error'))
    }
  }

  const handleSaveCommonRep = async () => {
    if (!unitId) return

    try {
      await updateCommonRep.mutateAsync({
        unitId,
        repOwnerId: selectedRepType === 'owner' ? selectedRepId : null,
        repContactId: selectedRepType === 'external' ? selectedRepId : null,
        designationDate: selectedRepType !== 'none' ? new Date().toISOString().split('T')[0] ?? null : null,
      })
      setSelectedRepType('none')
      setSelectedRepId(null)
    } catch (error) {
      console.error('Error updating common rep:', error)
      alert(t('common.error'))
    }
  }

  const handleRemoveCommonRep = async () => {
    if (!unitId) return

    try {
      await updateCommonRep.mutateAsync({
        unitId,
        repOwnerId: null,
        repContactId: null,
        designationDate: null,
      })
    } catch (error) {
      console.error('Error removing common rep:', error)
      alert(t('common.error'))
    }
  }

  const getUnitTypeColor = (type: string | null) => {
    switch (type) {
      case 'RESIDENTIAL':
        return 'blue'
      case 'PARKING':
        return 'purple'
      case 'CELLAR':
        return 'gray'
      case 'COMMERCIAL':
        return 'green'
      default:
        return 'gray'
    }
  }

  // Removed unused function getFloorDisplay

  const handleInteractOutside = (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    const target = e.detail.originalEvent.target as HTMLElement

    // Chercher la ligne du tableau cliquée (uniquement les lignes de données, pas le header)
    const tableRow = target.closest('[data-unit-row]') as HTMLElement
    const unitCard = target.closest('[data-unit-card]') as HTMLElement

    if (tableRow) {
      // Récupérer l'ID du unit depuis la ligne
      // La ligne a un onClick qui sera déclenché, donc on ne fait rien ici
      // Le handleUnitClick parent sera appelé automatiquement
      return
    }

    if (unitCard) {
      // Même chose pour les cards
      return
    }

    // Clic vraiment en dehors (ni tableau ni cards) → Fermer le drawer
    onClose()
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="800px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
          <Box display="flex" alignItems="center" w="full" gap={3}>
            {/* Navigation arrows - Left */}
            <Box display="flex" gap={1} flexShrink={0}>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onNavigatePrevious}
                disabled={!hasPrevious}
                aria-label={t('common.previous')}
                title={t('common.previous')}
              >
                <ChevronUp size={18} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onNavigateNext}
                disabled={!hasNext}
                aria-label={t('common.next')}
                title={t('common.next')}
              >
                <ChevronDown size={18} />
              </IconButton>
            </Box>

            {/* Title - Center */}
            <Box flex={1} minW={0}>
              {unit && (
                <>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" lineHeight="1.3">
                    {unit.residences?.name || 'Résidence'}
                  </Text>
                  <Heading
                    size="md"
                    lineHeight="1.3"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    Lot {unit.unit_number}
                  </Heading>
                  {unit.unit_type && (
                    <HStack gap={1.5} mt={1}>
                      <Badge colorPalette={getUnitTypeColor(unit.unit_type)} size="sm">
                        {t(`units.unit_types.${unit.unit_type}`)}
                      </Badge>
                    </HStack>
                  )}
                </>
              )}
              {!unit && <Heading size="md">{t('units.details')}</Heading>}
            </Box>

            {/* Actions menu and close - Right */}
            <Box display="flex" gap={2} flexShrink={0}>
              {unit && (
                <Box position="relative">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.actions')}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minW="180px" zIndex={10}>
                        <MenuItem
                          value="mutations"
                          onClick={() => setMutationsManagementOpen(true)}
                        >
                          <HStack gap={2}>
                            <ArrowRightLeft size={16} />
                            <Text>Gérer les mutations</Text>
                          </HStack>
                        </MenuItem>
                        {canDelete() && (
                          <MenuItem
                            value="delete"
                            onClick={handleDelete}
                            color="red.600"
                            _hover={{ bg: 'red.50' }}
                          >
                            <HStack gap={2}>
                              <Trash2 size={16} />
                              <Text>{t('common.delete')}</Text>
                            </HStack>
                          </MenuItem>
                        )}
                      </MenuContent>
                    </MenuPositioner>
                  </MenuRoot>
                </Box>
              )}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
                <X size={18} />
              </IconButton>
            </Box>
          </Box>
        </Drawer.Header>

        <Drawer.Body p={0}>
          {isLoading && <Box p={6}><LoadingState message={t('common.loading')} /></Box>}
          {error && <Box p={6}><ErrorState message={t('common.error')} /></Box>}
          {unit && (
            <>
              {/* Timestamps */}
              <HStack
                gap={3}
                px={6}
                pt={4}
                pb={3}
                bg="gray.50"
                borderBottomWidth="1px"
                borderColor="gray.200"
                flexWrap="wrap"
              >
                <HStack gap={2} flex={1} minW="200px">
                  <Box color="blue.500">
                    <CalendarClock size={16} />
                  </Box>
                  <VStack align="flex-start" gap={0}>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      {t('common.created_on')}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                      {new Date(unit.created_at ?? '').toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </Text>
                  </VStack>
                </HStack>

                {unit.updated_at && unit.updated_at !== unit.created_at && (
                  <HStack gap={2} flex={1} minW="200px">
                    <Box color="green.500">
                      <Clock size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {t('common.last_updated')}
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {new Date(unit.updated_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Text>
                    </VStack>
                  </HStack>
                )}
              </HStack>

              <Box px={6} py={6}>
                <VStack align="stretch" gap={6}>
              {/* Cards Identification et Propriétaires */}
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                {/* Card Identification */}
                <Card.Root variant="outline">
                  <Card.Header>
                    <Heading size="sm">Identification</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack align="stretch" gap={4}>
                      <EditableField
                        label={t('units.fields.unit_number')}
                        value={unit.unit_number}
                        fieldName="unit_number"
                        onUpdate={handleFieldUpdate}
                      />
                      <EditableSelectField
                        label={t('units.fields.unit_type')}
                        value={unit.unit_type}
                        fieldName="unit_type"
                        options={unitTypeOptions}
                        onUpdate={handleFieldUpdate}
                      />
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Card Propriétaires actuels */}
                <Card.Root variant="outline">
                  <Card.Header>
                    <Heading size="sm">Propriétaire(s) actuel(s)</Heading>
                  </Card.Header>
                  <Card.Body>
                    {ownerships && ownerships.length > 0 ? (
                      <VStack align="stretch" gap={4}>
                        {ownerships.map((ownership, index) => (
                          <VStack
                            key={ownership.id}
                            align="stretch"
                            gap={2}
                            pb={index < ownerships.length - 1 ? 4 : 0}
                            borderBottomWidth={index < ownerships.length - 1 ? '1px' : 0}
                            borderColor="gray.200"
                          >
                            <HStack gap={2} flexWrap="wrap">
                              <User size={16} color="var(--chakra-colors-brand-500)" />
                              <Text fontWeight="semibold" fontSize="sm">
                                {ownership.owners?.first_name} {ownership.owners?.last_name}
                              </Text>
                              <HStack gap={1} ml="auto" flexWrap="wrap">
                                {ownership.ownership_type && ownershipTypeConfig[ownership.ownership_type] && (
                                  <Badge
                                    colorPalette={ownershipTypeConfig[ownership.ownership_type]!.color}
                                    size="sm"
                                  >
                                    {ownershipTypeConfig[ownership.ownership_type]!.label}
                                  </Badge>
                                )}
                                {!(ownership.share_numerator === 1 && ownership.share_denominator === 1) && (
                                  <Badge colorPalette="gray" size="sm" variant="outline">
                                    {ownership.share_numerator}/{ownership.share_denominator}
                                  </Badge>
                                )}
                              </HStack>
                            </HStack>
                            {ownership.owners?.email && (
                              <HStack gap={2} fontSize="xs" color="gray.600">
                                <Mail size={12} />
                                <Text>{ownership.owners.email}</Text>
                              </HStack>
                            )}
                            {ownership.owners?.phone && (
                              <HStack gap={2} fontSize="xs" color="gray.600">
                                <Phone size={12} />
                                <Text>{ownership.owners.phone}</Text>
                              </HStack>
                            )}
                          </VStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text color="gray.500" fontSize="sm">
                        Aucun propriétaire enregistré
                      </Text>
                    )}
                  </Card.Body>
                </Card.Root>
              </Grid>

              {/* Section Mandataire Commun - uniquement pour les lots en indivision */}
              {isIndivision && (
                <Card.Root variant="outline" borderColor={hasCommonRep ? 'green.200' : 'orange.200'}>
                  <Card.Header bg={hasCommonRep ? 'green.50' : 'orange.50'}>
                    <HStack gap={2}>
                      <Users size={18} color={hasCommonRep ? 'var(--chakra-colors-green-600)' : 'var(--chakra-colors-orange-600)'} />
                      <Heading size="sm">Mandataire commun</Heading>
                      <Badge colorPalette="purple" size="sm" ml="auto">
                        Indivision ({ownerships?.length} indivisaires)
                      </Badge>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack align="stretch" gap={4}>
                      {/* Alerte si pas de mandataire */}
                      {!hasCommonRep && (
                        <Alert.Root status="warning">
                          <Alert.Indicator>
                            <AlertTriangle />
                          </Alert.Indicator>
                          <Alert.Content>
                            <Alert.Title fontWeight="semibold">Mandataire requis</Alert.Title>
                            <Alert.Description>
                              Ce lot est en indivision. Un mandataire commun doit être désigné pour recevoir les convocations et représenter les indivisaires lors des assemblées générales.
                            </Alert.Description>
                          </Alert.Content>
                        </Alert.Root>
                      )}

                      {/* Affichage du mandataire actuel */}
                      {hasCommonRep && (
                        <Box p={4} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                          <HStack gap={3} justify="space-between">
                            <HStack gap={3}>
                              <Box p={2} bg="green.100" borderRadius="full">
                                <UserCheck size={20} color="var(--chakra-colors-green-600)" />
                              </Box>
                              <VStack align="start" gap={0}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {commonRepOwner
                                    ? `${commonRepOwner.first_name} ${commonRepOwner.last_name}`
                                    : commonRepContact
                                      ? `${commonRepContact.first_name} ${commonRepContact.last_name}`
                                      : 'Mandataire désigné'}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {commonRepOwner ? 'Indivisaire' : 'Contact externe'}
                                  {unit?.common_rep_designation_date && (
                                    <> · Désigné le {new Date(unit.common_rep_designation_date).toLocaleDateString('fr-FR')}</>
                                  )}
                                </Text>
                                {(commonRepOwner?.email || commonRepContact?.email) && (
                                  <HStack gap={1} mt={1}>
                                    <Mail size={12} />
                                    <Text fontSize="xs" color="gray.600">
                                      {commonRepOwner?.email || commonRepContact?.email}
                                    </Text>
                                  </HStack>
                                )}
                              </VStack>
                            </HStack>
                            <IconButton
                              variant="ghost"
                              size="sm"
                              colorPalette="red"
                              onClick={handleRemoveCommonRep}
                              aria-label="Retirer le mandataire"
                              title="Retirer le mandataire"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </HStack>
                        </Box>
                      )}

                      {/* Formulaire de sélection du mandataire */}
                      {!hasCommonRep && (
                        <VStack align="stretch" gap={3}>
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">
                            Désigner un mandataire :
                          </Text>

                          {/* Sélection du type */}
                          <NativeSelectRoot size="sm">
                            <NativeSelectField
                              value={selectedRepType}
                              onChange={(e) => {
                                setSelectedRepType(e.target.value as 'none' | 'owner' | 'external')
                                setSelectedRepId(null)
                              }}
                            >
                              <option value="none">Sélectionner...</option>
                              <option value="owner">Un des indivisaires</option>
                              <option value="external">Un contact externe</option>
                            </NativeSelectField>
                          </NativeSelectRoot>

                          {/* Sélection de l'indivisaire */}
                          {selectedRepType === 'owner' && ownerships && (
                            <NativeSelectRoot size="sm">
                              <NativeSelectField
                                value={selectedRepId || ''}
                                onChange={(e) => setSelectedRepId(e.target.value || null)}
                                placeholder="Choisir un indivisaire"
                              >
                                <option value="">Choisir un indivisaire</option>
                                {ownerships.map((o) => (
                                  <option key={o.owners?.id} value={o.owners?.id || ''}>
                                    {`${o.owners?.first_name || ''} ${o.owners?.last_name || ''}`.trim()}
                                  </option>
                                ))}
                              </NativeSelectField>
                            </NativeSelectRoot>
                          )}

                          {/* Sélection du contact externe */}
                          {selectedRepType === 'external' && (
                            <>
                              {externalContacts && externalContacts.length > 0 ? (
                                <NativeSelectRoot size="sm">
                                  <NativeSelectField
                                    value={selectedRepId || ''}
                                    onChange={(e) => setSelectedRepId(e.target.value || null)}
                                    placeholder="Choisir un contact externe"
                                  >
                                    <option value="">Choisir un contact externe</option>
                                    {externalContacts.map((c) => (
                                      <option key={c.id} value={c.id}>
                                        {`${c.first_name || ''} ${c.last_name || ''}`.trim()}
                                      </option>
                                    ))}
                                  </NativeSelectField>
                                </NativeSelectRoot>
                              ) : (
                                <VStack align="stretch" gap={3}>
                                  <Alert.Root status="info">
                                    <Alert.Indicator>
                                      <Info />
                                    </Alert.Indicator>
                                    <Alert.Content>
                                      <Alert.Title fontWeight="semibold">Aucun contact externe</Alert.Title>
                                      <Alert.Description>
                                        Aucun contact externe n'est enregistré pour cette résidence.
                                      </Alert.Description>
                                    </Alert.Content>
                                  </Alert.Root>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setContactModalOpen(true)}
                                  >
                                    <Plus size={16} />
                                    Créer un contact externe
                                  </Button>
                                </VStack>
                              )}
                            </>
                          )}

                          {/* Bouton de sauvegarde */}
                          {selectedRepType !== 'none' && selectedRepId && (
                            <Button
                              colorPalette="green"
                              size="sm"
                              onClick={handleSaveCommonRep}
                              loading={updateCommonRep.isPending}
                            >
                              <UserCheck size={16} />
                              Désigner comme mandataire
                            </Button>
                          )}
                        </VStack>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>
              )}

              {/* General Info */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.sections.general_info')}</Heading>
                </Card.Header>
                <Card.Body>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <EditableField
                      label={t('units.fields.floor')}
                      value={unit.floor}
                      fieldName="floor"
                      type="number"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.stairwell')}
                      value={unit.stairwell}
                      fieldName="stairwell"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.area_sqm')}
                      value={unit.area_sqm}
                      fieldName="area_sqm"
                      type="number"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                  </Grid>
                </Card.Body>
              </Card.Root>

              {/* Shares */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.sections.shares')}</Heading>
                </Card.Header>
                <Card.Body>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <EditableField
                      label={t('units.fields.general_shares')}
                      value={unit.general_shares}
                      fieldName="general_shares"
                      type="number"
                      placeholder="0"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.work_fund_share')}
                      value={unit.work_fund_share}
                      fieldName="work_fund_share"
                      type="number"
                      placeholder="0"
                      onUpdate={handleFieldUpdate}
                    />
                  </Grid>
                </Card.Body>
              </Card.Root>

              {/* Notes */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.fields.notes')}</Heading>
                </Card.Header>
                <Card.Body>
                  <EditableField
                    label=""
                    value={unit.notes}
                    fieldName="notes"
                    placeholder="Aucune note"
                    onUpdate={handleFieldUpdate}
                  />
                </Card.Body>
              </Card.Root>
                </VStack>
              </Box>
            </>
          )}
        </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>

      {/* Modale de gestion des mutations */}
      {unitId && unit && ownerships && (
        <UnitMutationsManagementModal
          isOpen={mutationsManagementOpen}
          onClose={() => setMutationsManagementOpen(false)}
          unitId={unitId}
          unitNumber={unit.unit_number}
          currentOwners={ownerships.map((ownership) => ({
            ownerId: ownership.owner_id,
            ownerName: `${ownership.owners?.first_name || ''} ${ownership.owners?.last_name || ''}`.trim() || 'Sans nom',
            ownershipId: ownership.id,
            shareNumerator: ownership.share_numerator,
            shareDenominator: ownership.share_denominator,
            ownershipType: (ownership.ownership_type as 'full' | 'usufruct' | 'bare') || 'full',
          }))}
        />
      )}

      {/* Modale de création de contact externe */}
      {unit?.residence_id && (
        <ExternalContactModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          residenceId={unit.residence_id}
          onSuccess={(contact) => {
            setSelectedRepId(contact.id)
          }}
        />
      )}
    </Drawer.Root>
  )
}
